import React, { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useApi } from '../../hooks/useApi'
import { useRedemptionPolling } from '../../hooks/usePolling'
import StatusBadge from '../../components/StatusBadge'
import { CheckCircle2, XCircle, Loader2, Clock, Ticket, ArrowLeft } from 'lucide-react'

const RedeemStatus = () => {
  const { redemptionId } = useParams()
  const api = useApi()
  const pollFn = useMemo(() => redemptionId ? () => api.getRedemptionStatus(redemptionId) : null, [redemptionId])
  const { data, loading, error, isPolling } = useRedemptionPolling(redemptionId, api)

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—'

  if (loading && !data) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <Loader2 size={32} className="mx-auto animate-spin text-accent mb-3" />
        <p className="text-text-muted">Loading status...</p>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="container mx-auto px-4 py-20">
        <div className="card max-w-sm mx-auto text-center">
          <Clock size={32} className="mx-auto text-warning mb-3" />
          <h2 className="text-lg font-semibold mb-1">
            {error.message === 'Polling timeout' ? 'Taking longer than expected' : 'Could not load status'}
          </h2>
          <p className="text-text-muted text-sm mb-4">Please check back shortly.</p>
          <Link to="/" className="btn btn-primary">Back to Products</Link>
        </div>
      </div>
    )
  }

  if (!data) return null
  const isFinal = ['completed', 'failed'].includes(data.status)

  return (
    <div className="container mx-auto px-4 py-8 max-w-md">
      <div className="card animate-fade-in">
        {/* Status icon */}
        <div className="text-center mb-6">
          {data.status === 'completed' && <CheckCircle2 size={48} className="mx-auto text-success mb-2" />}
          {data.status === 'failed' && <XCircle size={48} className="mx-auto text-error mb-2" />}
          {!isFinal && <Loader2 size={48} className="mx-auto text-accent animate-spin mb-2" />}

          <h2 className="text-xl font-bold text-text-primary">
            {data.status === 'completed' ? 'Redemption Successful' : data.status === 'failed' ? 'Redemption Failed' : 'Processing...'}
          </h2>
          <div className="mt-2"><StatusBadge status={data.status} /></div>
        </div>

        {/* Details */}
        <div className="bg-gray-50 rounded-lg p-4 divide-y divide-border mb-5">
          {[
            data.product_name && ['Product', data.product_name],
            data.denomination && ['Value', data.denomination],
            data.code_masked && ['Code', data.code_masked],
            data.user_input && ['Delivered To', data.user_input],
            data.redeemed_at && ['Redeemed', formatDate(data.redeemed_at)],
            data.completed_at && ['Completed', formatDate(data.completed_at)],
          ].filter(Boolean).map(([label, val]) => (
            <div key={label} className="flex justify-between py-2.5 first:pt-0 last:pb-0">
              <span className="text-text-muted text-sm">{label}</span>
              <span className={`text-sm font-medium ${label === 'Code' ? 'font-mono text-text-secondary' : 'text-text-primary'}`}>{val}</span>
            </div>
          ))}
        </div>

        {data.status === 'failed' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
            <p className="text-amber-800 text-sm font-medium mb-1">Your code is still valid</p>
            <p className="text-amber-700 text-xs">The order failed but your code has been restored. You can use it again.</p>
            {data.error_message && <p className="text-red-600 text-xs mt-2">{data.error_message}</p>}
          </div>
        )}

        {isPolling && !isFinal && (
          <p className="text-text-muted text-xs text-center mb-4 flex items-center justify-center space-x-1">
            <Loader2 size={12} className="animate-spin" />
            <span>Checking status...</span>
          </p>
        )}

        <Link to="/" className="btn btn-primary w-full">
          <Ticket size={16} className="mr-2" />
          {isFinal ? 'Redeem Another Code' : 'Back to Products'}
        </Link>
      </div>
    </div>
  )
}

export default RedeemStatus
